import { Form, Formik } from 'formik';
import { DateTime } from 'luxon';
import { InferType } from 'yup';
import React, { ReactNode, useState } from 'react';
import Button from '../common/Button';
import { ErrorText, FormikInput, FormikTextArea, DateTimePicker, Label } from '../common/Form';
import { useAnnouncementsContext } from '../../contexts/AnnouncementsContext';
import { ModalAnnouncementSchema } from './AnnouncementModal';
import { MediaLibraryInterface, MenuItemMediaInterface } from '../../types/MediaInterface';
import MediaPreview from '../MediaPreview';
import MediaLibraryModal from '../MediaLibraryModal';
import imageDisplay from '../../assets/images/ImageDisplay.png';

interface ModalAnnouncementFormProps {
  children: ReactNode | ReactNode[];
  announcementID?: number;
  image?: MenuItemMediaInterface;
  isEdit: boolean;
  errorMessage: string;
  onSubmit: Function;
  // eslint-disable-next-line no-unused-vars
  onImageUpload: (_image: MenuItemMediaInterface) => void;
  onTitleChange: Function;
  onDescriptionChange: Function;
  onCancel: Function;
  onEdited: Function;
  formikRef: any;
  isDesktop?: boolean;
}

const ModalAnnouncementForm = ({
  children,
  announcementID,
  image,
  isEdit,
  errorMessage,
  onSubmit,
  onImageUpload,
  onTitleChange,
  onDescriptionChange,
  onCancel,
  onEdited,
  formikRef,
  isDesktop
}: ModalAnnouncementFormProps) => {
  const { getAnnouncement } = useAnnouncementsContext();
  const [openMediaLibraryModal, setOpenMediaLibraryModal] = useState<boolean>(false);

  const handleImageUpload = (_media: MediaLibraryInterface[]) => {
    onImageUpload(
      _media?.[0] ? { mediaID: _media?.[0].mediaID, mediaURL: _media?.[0].mediaUrl, type: 'image' } : undefined
    );
  };

  const handleTitleValChanged = (value: string) => {
    onTitleChange(value);
  };

  const handleDescriptionValChanged = (value: string) => {
    onDescriptionChange(value);
  };

  const getInitialValues = () => {
    if (isEdit) {
      const announcement = getAnnouncement(announcementID);
      return {
        name: announcement.name,
        title: announcement.title,
        description: announcement.description,
        startDate: announcement.startDate,
        endDate: announcement.endDate
      };
    }
    return ModalAnnouncementSchema.cast({
      startDate: DateTime.fromJSDate(new Date()).toISO({ includeOffset: false }),
      endDate: DateTime.fromJSDate(new Date()).plus({ days: 1 }).toISO({ includeOffset: false })
    });
  };

  return (
    <Formik
      initialValues={getInitialValues()}
      validationSchema={ModalAnnouncementSchema}
      onSubmit={(data: InferType<typeof ModalAnnouncementSchema>) => onSubmit(data)}
      innerRef={formikRef}
    >
      {({ values }) => (
        <Form>
          <div className="announcement-form-container">
            <div className="announcement-inputs-container">
              <div className="announcement-name-container">
                <FormikInput name="name" label="PROMOTION NAME" className="announcement-input" onBlur={onEdited} />
                <p className="announcement-name-copy">
                  For your reference only. The name is not displayed on the promotion.
                </p>
              </div>
              {children}
            </div>
            <div className="announcement-timespan-input">
              <DateTimePicker name="startDate" label="START DATE" className="start-input" />
              <DateTimePicker name="endDate" label="END DATE" minDate={values?.startDate} />
            </div>
            <div className="announcement-content-inputs-container">
              <FormikInput
                name="title"
                label="HEADING TEXT"
                className="announcement-input"
                onBlur={onEdited}
                onChange={handleTitleValChanged}
              />
              <FormikTextArea
                name="description"
                label="DESCRIPTION"
                className="announcement-area"
                onBlur={onEdited}
                onChange={handleDescriptionValChanged}
              />
            </div>
            {isDesktop ? (
              <div className="announcement-image-container">
                <Label
                  label={
                    <span>
                      PHOTO <i>(Optional)</i>
                    </span>
                  }
                  className="announcement-image-label"
                />
                <Button className="select-media-button" onClick={() => setOpenMediaLibraryModal(true)}>
                  Select Media
                </Button>
                <MediaPreview media={image ? [image] : []} onUpdate={handleImageUpload} multiple={false} />
                {openMediaLibraryModal && (
                  <MediaLibraryModal
                    selectedMediaIDs={image ? [image.mediaID] : []}
                    onSelect={handleImageUpload}
                    onRemove={() => onImageUpload(undefined)}
                    onClose={() => setOpenMediaLibraryModal(false)}
                    mediaTypeOverride="image"
                  />
                )}
                <p className="announcement-image-copy">
                  Photos should be no larger than 750 KB & have an aspect ratio of 3:2.
                </p>
              </div>
            ) : (
              <div className="announcement-image-container">
                <Label
                  label={
                    <span>
                      MEDIA <i>(OPTIONAL)</i>
                    </span>
                  }
                  className="announcement-image-label"
                />
                <p className="announcement-image-copy">
                  Photos should be no larger than 750 KB & have an aspect ratio of 3:2.
                </p>
                <div className="media-container">
                  <Button className="select-media-button" onClick={() => setOpenMediaLibraryModal(true)}>
                    <img className="media-upload-icon" src={imageDisplay} alt="Icon indicating upload" />
                    <p>Select Media</p>
                  </Button>
                  <MediaPreview media={image ? [image] : []} onUpdate={handleImageUpload} multiple={false} />
                </div>
                {openMediaLibraryModal && (
                  <MediaLibraryModal
                    selectedMediaIDs={image ? [image.mediaID] : []}
                    onSelect={handleImageUpload}
                    onRemove={() => onImageUpload(undefined)}
                    onClose={() => setOpenMediaLibraryModal(false)}
                    mediaTypeOverride="image"
                  />
                )}
              </div>
            )}
          </div>
          {errorMessage && <ErrorText error={errorMessage} />}
          <div className="modal-footer">
            <Button onClick={onCancel} className="modal-cancel-button">
              CANCEL
            </Button>
            <Button submit className="modal-confirm-button">
              CONFIRM
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default ModalAnnouncementForm;
